import React from 'react';
import { X, Sparkles } from 'lucide-react';
import { ChatMessage as ChatMessageType, ChatAction } from '../../../types/chat';
import { ChatMessagesArea } from './ChatMessagesArea';
import { ChatInputArea } from './ChatInputArea';
import { CHAT_TEXT } from '../../../constants/chat';

interface ChatPanelSlidingProps {
  isOpen: boolean;
  onClose: () => void;
  messages: ChatMessageType[];
  isLoading: boolean;
  isLoadingHistory: boolean;
  inputMessage: string;
  onInputChange: (value: string) => void;
  onSend: () => void;
  onKeyPress: (e: React.KeyboardEvent) => void;
  onExecuteAction: (action: ChatAction) => void;
  panelInputRef: React.RefObject<HTMLTextAreaElement>;
  messagesEndRef: React.RefObject<HTMLDivElement>;
  adjustTextareaHeight: (textarea: HTMLTextAreaElement | null) => void;
}

/**
 * Sliding panel mode for chat - fixed position right-side panel
 * Shows messages and input, overlay backdrop for closing
 */
export const ChatPanelSliding: React.FC<ChatPanelSlidingProps> = ({
  isOpen,
  onClose,
  messages,
  isLoading,
  isLoadingHistory,
  inputMessage,
  onInputChange,
  onSend,
  onKeyPress,
  onExecuteAction,
  panelInputRef,
  messagesEndRef,
  adjustTextareaHeight,
}) => {
  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/20 z-40"
        onClick={onClose}
      />

      {/* Panel */}
      <div className="fixed top-0 right-0 bottom-0 w-[400px] bg-white shadow-xl z-50 flex flex-col animate-slideInRight">
        {/* Panel Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-gray-900 rounded-full flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <div>
              <h3 className="text-[14px] font-semibold text-gray-900">{CHAT_TEXT.BRAND_NAME}</h3>
              <p className="text-[11px] text-gray-500">{CHAT_TEXT.SUBTITLE}</p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close chat panel"
            className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Messages Area */}
        <ChatMessagesArea
          messages={messages}
          isLoading={isLoading}
          isLoadingHistory={isLoadingHistory}
          onExecuteAction={onExecuteAction}
          messagesEndRef={messagesEndRef}
          className="flex-1 bg-gray-50/30"
          variant="panel"
        />

        {/* Input Area */}
        <ChatInputArea
          inputMessage={inputMessage}
          onInputChange={onInputChange}
          onSend={onSend}
          onKeyPress={onKeyPress}
          isLoading={isLoading}
          inputRef={panelInputRef}
          adjustTextareaHeight={adjustTextareaHeight}
          variant="panel"
        />
      </div>
    </>
  );
};
